/**
 * DispatchFunnel — "today's dispatch picture":
 * demand → jobs → dispatch → coordinates. Renders the pure funnel-model VM;
 * every number carries its source caption and a drill-down of order names.
 * Statuses come from funnel-model exclusively — this component never decides
 * thresholds.
 */
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { http } from '@lib/api';
import { queryKeys } from '@lib/queryKeys';
import { EntityLink } from '@resource-kit/ui/react';
import type { components } from '@/types/api.generated';
import { buildFunnelModel, type FunnelStatus } from '../funnel-model';
import { useRoutePlanningStops } from '../hooks/useRoutePlanningStops';

type SalesBridge = components['schemas']['SalesBridge'];
type DispatchPreview = components['schemas']['DispatchPreview'];
type RoutePlanningStops = components['schemas']['RoutePlanningStops'];
type MissingReason = RoutePlanningStops['missing'][number]['reason'];

const MISSING_REASON_LABELS = {
  order_not_found: 'routePlanning.missingReason.order_not_found',
  no_address: 'routePlanning.missingReason.no_address',
  unresolved: 'routePlanning.missingReason.unresolved',
} as const satisfies Record<MissingReason, string>;

const STATUS_DOT: Record<FunnelStatus, string> = {
  alert: 'bg-destructive',
  ok: 'bg-success',
  neutral: 'bg-muted-foreground/40',
};

function StatusDot({ status }: { status: FunnelStatus }) {
  return (
    <span
      aria-hidden
      className={`inline-block h-2 w-2 shrink-0 rounded-full ${STATUS_DOT[status]}`}
    />
  );
}

function FunnelRow(props: {
  status: FunnelStatus;
  label: string;
  summary: string;
  source: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 border-b border-border/60 py-3 last:border-b-0">
      <div className="flex items-center gap-2">
        <StatusDot status={props.status} />
        <span className="w-16 shrink-0 text-sm font-medium">{props.label}</span>
        <span className="text-sm">{props.summary}</span>
      </div>
      <p className="pl-4 text-xs text-muted-foreground">{props.source}</p>
      {props.children}
    </div>
  );
}

// Each drill-down row links to the surface that can ACT on the thing it names
// — an order row to the order page, a job row to the job page (#1675) — so the
// caller supplies the target and this component never guesses a route prefix.
function DrillDown(props: {
  title: string;
  items: Array<{ key: string; label: string; to?: string }>;
}) {
  if (props.items.length === 0) return null;
  return (
    <details className="pl-4 text-sm">
      <summary className="cursor-pointer text-xs text-muted-foreground">
        {props.title} ({props.items.length})
      </summary>
      <ul className="mt-1 grid grid-cols-2 gap-x-4 sm:grid-cols-3">
        {props.items.map((item) => (
          <li key={item.key} className="font-mono text-xs">
            {item.to === undefined ? (
              item.label
            ) : (
              <EntityLink to={item.to}>{item.label}</EntityLink>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}

export function DispatchFunnel({ date }: { date: string }) {
  const { t } = useTranslation();
  const bridgeQuery = useQuery({
    queryKey: queryKeys.dispatch.salesBridge(date),
    queryFn: () =>
      http.get<SalesBridge>('/api/dispatch/sales-bridge', {
        params: { date },
      }),
    staleTime: 60_000,
  });
  const previewQuery = useQuery({
    queryKey: queryKeys.dispatch.preview(date),
    queryFn: () =>
      http.get<DispatchPreview>('/api/dispatch/preview', {
        params: { date },
      }),
    enabled: !!date,
    staleTime: 60 * 1000,
  });
  const stopsQuery = useRoutePlanningStops(date);
  if (
    bridgeQuery.data === undefined ||
    previewQuery.data === undefined ||
    stopsQuery.data === undefined
  ) {
    return null;
  }
  const vm = buildFunnelModel({
    bridge: bridgeQuery.data,
    preview: previewQuery.data,
    stops: stopsQuery.data,
  });
  const { demand, jobs, dispatch, coordinates } = vm;
  return (
    <section className="rounded-lg border bg-card p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{t('dispatch.funnel.title')}</h3>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <StatusDot status={dispatch.status} />
          {dispatch.undispatched === 0
            ? t('dispatch.funnel.dispatch.done')
            : dispatch.pastCutoff
              ? t('dispatch.funnel.dispatch.afterCutoff', {
                  at: dispatch.cutoffTime,
                  count: dispatch.undispatched,
                })
              : t('dispatch.funnel.dispatch.beforeCutoff', {
                  at: dispatch.cutoffTime,
                })}
        </span>
      </div>
      <FunnelRow
        status={demand.status}
        label={t('dispatch.funnel.demand.label')}
        summary={t('dispatch.funnel.demand.summary', {
          orders: demand.orders,
          units: demand.units,
        })}
        source={t('dispatch.funnel.demand.source')}
      >
        {demand.sameProductMultiQtyOrders > 0 && (
          <p className="pl-4 text-xs text-muted-foreground">
            {t('dispatch.funnel.demand.sameProductNote', {
              count: demand.sameProductMultiQtyOrders,
            })}
          </p>
        )}
        {demand.excludedOrders > 0 && (
          <p className="pl-4 text-xs text-muted-foreground">
            {t('dispatch.funnel.demand.excludedNote', {
              orders: demand.excludedOrders,
              units: demand.excludedUnits,
            })}
          </p>
        )}
      </FunnelRow>
      <FunnelRow
        status={jobs.status}
        label={t('dispatch.funnel.jobs.label')}
        summary={
          jobs.gap > 0
            ? t('dispatch.funnel.jobs.gap', { count: jobs.jobs, gap: jobs.gap })
            : t('dispatch.funnel.jobs.ok', { count: jobs.jobs })
        }
        source={t('dispatch.funnel.jobs.source')}
      >
        <DrillDown
          title={t('dispatch.funnel.jobs.drilldownTitle')}
          items={jobs.gapOrders.map((entry) => ({
            key: entry.orderName,
            label: `${entry.orderName} ×${entry.units}`,
            to: `/orders/${entry.orderId}`,
          }))}
        />
      </FunnelRow>
      <FunnelRow
        status={dispatch.status}
        label={t('dispatch.funnel.dispatch.label')}
        summary={t('dispatch.funnel.dispatch.summary', {
          dispatched: dispatch.dispatched,
          undispatched: dispatch.undispatched,
        })}
        source={t('dispatch.funnel.dispatch.source')}
      >
        <DrillDown
          title={t('dispatch.funnel.dispatch.drilldownTitle')}
          items={dispatch.undispatchedJobs.map((entry) => ({
            key: entry.jobId,
            label: entry.jobName,
            to: `/dispatch/jobs/${entry.jobId}`,
          }))}
        />
      </FunnelRow>
      <FunnelRow
        status={coordinates.status}
        label={t('dispatch.funnel.coordinates.label')}
        summary={t('dispatch.funnel.coordinates.summary', {
          ready: coordinates.ready,
          pending: coordinates.pending,
        })}
        source={t('dispatch.funnel.coordinates.source')}
      >
        {coordinates.pending > 0 && (
          <p className="pl-4 text-xs text-muted-foreground">
            {t('dispatch.funnel.coordinates.pendingNote')}
          </p>
        )}
        {coordinates.alerts.length > 0 && (
          <p className="pl-4 text-xs text-destructive">
            {t('dispatch.funnel.coordinates.alert', {
              count: coordinates.alerts.length,
            })}
          </p>
        )}
        <DrillDown
          title={t('dispatch.funnel.coordinates.drilldownTitle')}
          items={coordinates.alerts.map((alert) => ({
            key: alert.jobId,
            label: `${alert.orderName || t('dispatch.funnel.coordinates.orderMissing')} · ${t(MISSING_REASON_LABELS[alert.reason])}`,
            to: alert.orderId === undefined ? undefined : `/orders/${alert.orderId}`,
          }))}
        />
      </FunnelRow>
    </section>
  );
}
